import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardList, Loader2, Plus, Save, Search, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  deleteAttendanceChecklist,
  listAttendanceChecklists,
  saveAttendanceChecklist,
} from "../../../utils/attendanceChecklistStore";

const FREQUENCIES = [
  { value: "monthly", label: "Monthly", hint: "Every attendance month" },
  { value: "one_time", label: "One-time", hint: "Single specific month" },
  { value: "selected_months", label: "Selected months", hint: "Pick calendar months" },
  { value: "quarterly", label: "Quarterly", hint: "Default Mar / Jun / Sep / Dec" },
  { value: "half_yearly", label: "Half-yearly", hint: "Default Apr / Oct" },
  { value: "yearly", label: "Yearly", hint: "Once a year" },
  { value: "date_range", label: "Date range", hint: "Inclusive from–to months" },
];

const MONTH_OPTS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: new Date(2020, i, 1).toLocaleString("en", { month: "short" }),
}));

function emptyMeta(freq) {
  if (freq === "one_time") return { oneTimeMonth: "" };
  if (freq === "selected_months") return { months: [] };
  if (freq === "quarterly") return { months: [3, 6, 9, 12] };
  if (freq === "half_yearly") return { months: [4, 10] };
  if (freq === "yearly") return { month: 3 };
  if (freq === "date_range") return { fromMonth: "", toMonth: "" };
  return {};
}

function newItem(label = "") {
  return {
    key: `item_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    label,
    required: true,
    linkType: "",
    linkPath: "",
    linkLabel: "",
  };
}

function blankDraft() {
  return {
    id: null,
    name: "",
    description: "",
    frequency: "monthly",
    specificMonthsJson: {},
    itemsJson: [newItem()],
    active: true,
    sortOrder: 0,
  };
}

function schedulePreview(frequency, meta = {}) {
  const freq = frequency || "monthly";
  if (freq === "monthly" || freq === "once_per_month" || freq === "recurring") {
    return "Due every attendance month";
  }
  if (freq === "one_time") {
    return meta.oneTimeMonth ? `Due only in ${meta.oneTimeMonth}` : "Pick a month";
  }
  if (["selected_months", "quarterly", "half_yearly"].includes(freq)) {
    const months = Array.isArray(meta.months) ? meta.months.map(Number) : [];
    if (!months.length) return "No months selected";
    return `Due in ${months
      .map((m) => MONTH_OPTS.find((o) => o.value === m)?.label || m)
      .join(", ")}`;
  }
  if (freq === "yearly") {
    const m = Number(meta.month || 3);
    return `Due every ${MONTH_OPTS.find((o) => o.value === m)?.label || m}`;
  }
  if (freq === "date_range") {
    if (!meta.fromMonth && !meta.toMonth) return "Set from / to months";
    return `${meta.fromMonth || "…"} → ${meta.toMonth || "…"}`;
  }
  return freq;
}

export default function AttendanceChecklistEditor({ darkMode = false, canEdit = true }) {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const border = darkMode ? "border-[#3C3C4C]" : "border-gray-200";
  const card = darkMode ? "bg-[#242424] border-[#3C3C4C]" : "bg-white border-gray-200";
  const muted = darkMode ? "text-gray-400" : "text-gray-500";
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[hsl(var(--ds-primary))] ${
    darkMode ? "border-[#4C4C5C] bg-[#2C2C2C] text-white" : "border-gray-300 bg-white"
  }`;

  const load = useCallback(() => {
    setLoading(true);
    try {
      const rows = listAttendanceChecklists();
      setChecklists(rows);
      setSelectedId((prev) => {
        if (prev && rows.some((r) => r.id === prev)) return prev;
        return rows[0]?.id ?? null;
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedId) {
      if (!draft || draft.id) {
        setDraft(null);
        setDirty(false);
      }
      return;
    }
    const row = checklists.find((c) => c.id === selectedId);
    if (!row) return;
    setDraft({
      id: row.id,
      name: row.name || "",
      description: row.description || "",
      frequency: row.frequency || "monthly",
      specificMonthsJson: row.specificMonthsJson || {},
      itemsJson:
        Array.isArray(row.itemsJson) && row.itemsJson.length ? row.itemsJson : [newItem()],
      active: row.active !== false,
      sortOrder: Number(row.sortOrder) || 0,
    });
    setDirty(false);
    setConfirmDelete(false);
  }, [selectedId, checklists]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return checklists.filter((c) => {
      if (filter === "active" && c.active === false) return false;
      if (filter === "inactive" && c.active !== false) return false;
      if (!q) return true;
      const hay = `${c.name || ""} ${c.description || ""} ${(c.itemsJson || [])
        .map((i) => i.label)
        .join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [checklists, search, filter]);

  const patchDraft = (patch) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
    setDirty(true);
  };

  const startCreate = () => {
    if (!canEdit) return;
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    setSelectedId(null);
    setDraft(blankDraft());
    setDirty(true);
    setConfirmDelete(false);
  };

  const handleSave = () => {
    if (!canEdit || !draft) return;
    if (!draft.name.trim()) {
      toast.error("Checklist name is required");
      return;
    }
    const items = (draft.itemsJson || [])
      .map((it) => ({
        ...it,
        label: String(it.label || "").trim(),
        key: it.key || newItem().key,
      }))
      .filter((it) => it.label);
    if (!items.length) {
      toast.error("Add at least one task");
      return;
    }
    const freq = draft.frequency || "monthly";
    const meta = draft.specificMonthsJson || {};
    if (freq === "one_time" && !meta.oneTimeMonth) {
      toast.error("Select the one-time month");
      return;
    }
    if (freq === "selected_months" && !(meta.months || []).length) {
      toast.error("Select at least one month");
      return;
    }

    setSaving(true);
    try {
      const saved = saveAttendanceChecklist({
        ...draft,
        name: draft.name.trim(),
        description: draft.description?.trim() || null,
        frequency: freq,
        specificMonthsJson: meta,
        itemsJson: items,
        active: draft.active !== false,
        sortOrder: Number(draft.sortOrder) || 0,
      });
      toast.success(draft.id ? "Checklist saved" : "Checklist created");
      load();
      if (saved?.id) setSelectedId(saved.id);
      setDirty(false);
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!canEdit || !draft?.id) return;
    setSaving(true);
    try {
      deleteAttendanceChecklist(draft.id);
      toast.success("Checklist deleted");
      setConfirmDelete(false);
      setDirty(false);
      setSelectedId(null);
      setDraft(null);
      load();
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (idx, patch) => {
    const items = [...(draft.itemsJson || [])];
    items[idx] = { ...items[idx], ...patch };
    patchDraft({ itemsJson: items });
  };

  const removeItem = (idx) => {
    const items = (draft.itemsJson || []).filter((_, i) => i !== idx);
    patchDraft({ itemsJson: items.length ? items : [newItem()] });
  };

  const renderScheduleFields = () => {
    if (!draft) return null;
    const freq = draft.frequency || "monthly";
    const meta = draft.specificMonthsJson || {};
    const setMeta = (next) => patchDraft({ specificMonthsJson: next });

    if (freq === "one_time") {
      return (
        <label className="block text-xs">
          <span className={muted}>Month</span>
          <input
            type="month"
            className={`${inputCls} mt-1`}
            value={meta.oneTimeMonth || ""}
            disabled={!canEdit}
            onChange={(e) => setMeta({ ...meta, oneTimeMonth: e.target.value })}
          />
        </label>
      );
    }

    if (["selected_months", "quarterly", "half_yearly"].includes(freq)) {
      const months = Array.isArray(meta.months) ? meta.months.map(Number) : [];
      return (
        <div>
          <p className={`mb-2 text-xs ${muted}`}>Select months</p>
          <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6">
            {MONTH_OPTS.map((m) => {
              const on = months.includes(m.value);
              return (
                <button
                  key={m.value}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => {
                    const next = on
                      ? months.filter((x) => x !== m.value)
                      : [...months, m.value].sort((a, b) => a - b);
                    setMeta({ ...meta, months: next });
                  }}
                  className={`rounded-lg border px-2 py-1.5 text-xs font-medium ${
                    on
                      ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.1)] text-[hsl(var(--ds-primary))]"
                      : darkMode
                      ? "border-[#4C4C5C] text-gray-300"
                      : "border-gray-200 text-gray-700"
                  }`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (freq === "yearly") {
      return (
        <label className="block text-xs">
          <span className={muted}>Month each year</span>
          <select
            className={`${inputCls} mt-1`}
            value={Number(meta.month || 3)}
            disabled={!canEdit}
            onChange={(e) => setMeta({ ...meta, month: Number(e.target.value) })}
          >
            {MONTH_OPTS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </label>
      );
    }

    if (freq === "date_range") {
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs">
            <span className={muted}>From</span>
            <input
              type="month"
              className={`${inputCls} mt-1`}
              value={meta.fromMonth || ""}
              disabled={!canEdit}
              onChange={(e) => setMeta({ ...meta, fromMonth: e.target.value })}
            />
          </label>
          <label className="block text-xs">
            <span className={muted}>To</span>
            <input
              type="month"
              className={`${inputCls} mt-1`}
              value={meta.toMonth || ""}
              disabled={!canEdit}
              onChange={(e) => setMeta({ ...meta, toMonth: e.target.value })}
            />
          </label>
        </div>
      );
    }

    return (
      <p className={`text-xs ${muted}`}>Appears on every Process Attendance month.</p>
    );
  };

  if (loading) {
    return (
      <div className={`flex h-48 items-center justify-center gap-2 rounded-xl border ${card}`}>
        <Loader2 className="animate-spin text-[hsl(var(--ds-primary))]" size={18} />
        <span className={`text-sm ${muted}`}>Loading checklist tasks…</span>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[480px] flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="ds-h3 text-[hsl(var(--ds-fg))]">
            Attendance Checklist
          </h2>
          <p className={`mt-0.5 text-xs ${muted}`}>
            Define tasks that appear on Process Attendance → Checklist for matching months.
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={startCreate}
            className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--ds-primary))] px-3 py-2 text-xs font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
          >
            <Plus size={14} /> New checklist
          </button>
        )}
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[280px_1fr]">
        <div className={`flex min-h-0 flex-col rounded-xl border ${card}`}>
          <div className={`border-b p-3 ${border}`}>
            <div className="relative mb-2">
              <Search
                size={14}
                className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`}
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className={`${inputCls} pl-8`}
              />
            </div>
            <div className="flex gap-1">
              {["all", "active", "inactive"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold capitalize ${
                    filter === f
                      ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                      : darkMode
                      ? "bg-[#333] text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto p-2">
            {filtered.length === 0 ? (
              <p className={`px-2 py-6 text-center text-xs ${muted}`}>No checklists yet</p>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    if (dirty && !window.confirm("Discard unsaved changes?")) return;
                    setSelectedId(c.id);
                  }}
                  className={`mb-1 flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-xs ${
                    selectedId === c.id
                      ? "bg-[hsl(var(--ds-primary)/0.12)] font-semibold text-[hsl(var(--ds-primary))]"
                      : "hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  <ClipboardList size={14} className="mt-0.5 shrink-0 text-[hsl(var(--ds-primary))]" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{c.name}</span>
                    <span className={`block truncate ${muted}`}>
                      {schedulePreview(c.frequency, c.specificMonthsJson)}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className={`min-h-0 overflow-auto rounded-xl border p-4 ${card}`}>
          {!draft ? (
            <p className={`py-12 text-center text-sm ${muted}`}>
              Select a checklist or create a new one.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-xs sm:col-span-2">
                  <span className={muted}>Name</span>
                  <input
                    className={`${inputCls} mt-1`}
                    value={draft.name}
                    disabled={!canEdit}
                    onChange={(e) => patchDraft({ name: e.target.value })}
                  />
                </label>
                <label className="block text-xs sm:col-span-2">
                  <span className={muted}>Description</span>
                  <textarea
                    rows={2}
                    className={`${inputCls} mt-1`}
                    value={draft.description || ""}
                    disabled={!canEdit}
                    onChange={(e) => patchDraft({ description: e.target.value })}
                  />
                </label>
                <label className="block text-xs">
                  <span className={muted}>Frequency</span>
                  <select
                    className={`${inputCls} mt-1`}
                    value={draft.frequency || "monthly"}
                    disabled={!canEdit}
                    onChange={(e) => {
                      const frequency = e.target.value;
                      patchDraft({
                        frequency,
                        specificMonthsJson: emptyMeta(frequency),
                      });
                    }}
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex items-end gap-2 pb-2 text-xs">
                  <input
                    type="checkbox"
                    checked={draft.active !== false}
                    disabled={!canEdit}
                    onChange={(e) => patchDraft({ active: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>

              {renderScheduleFields()}

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-semibold">Tasks</p>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() =>
                        patchDraft({
                          itemsJson: [...(draft.itemsJson || []), newItem()],
                        })
                      }
                      className="text-[11px] font-semibold text-[hsl(var(--ds-primary))]"
                    >
                      + Add task
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {(draft.itemsJson || []).map((it, idx) => (
                    <div
                      key={it.key || idx}
                      className={`flex flex-wrap items-start gap-2 rounded-lg border p-2 ${border}`}
                    >
                      <input
                        className={`${inputCls} min-w-[180px] flex-1`}
                        placeholder="Task label"
                        value={it.label || ""}
                        disabled={!canEdit}
                        onChange={(e) => updateItem(idx, { label: e.target.value })}
                      />
                      <label className="flex items-center gap-1 text-[11px]">
                        <input
                          type="checkbox"
                          checked={it.required !== false}
                          disabled={!canEdit}
                          onChange={(e) => updateItem(idx, { required: e.target.checked })}
                        />
                        Required
                      </label>
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          className="rounded-md p-1.5 text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-danger)/0.1)]"
                          title="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {canEdit && (
                <div className="flex flex-wrap items-center gap-2 border-t pt-3">
                  <button
                    type="button"
                    disabled={saving || !dirty}
                    onClick={handleSave}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--ds-primary))] px-3 py-2 text-xs font-semibold text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))] disabled:opacity-50"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save
                  </button>
                  {draft.id && (
                    confirmDelete ? (
                      <>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={handleDelete}
                          className="rounded-md bg-[hsl(var(--ds-danger))] px-3 py-2 text-xs font-semibold text-white hover:bg-[hsl(var(--ds-danger-hover))]"
                        >
                          Confirm delete
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(false)}
                          className="rounded-lg border px-3 py-2 text-xs"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(true)}
                        className="rounded-lg border border-red-300 px-3 py-2 text-xs font-semibold text-red-600"
                      >
                        Delete
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
